import React from 'react';
import type { TransformedReport } from '../types';

interface USPDetailSlideProps {
  data: TransformedReport;
}

/**
 * USPDetailSlide - Slide 5: USP detail with market/competitor/strength flow + positioning map.
 */
const USPDetailSlide: React.FC<USPDetailSlideProps> = ({ data }) => {
  const usp = data.usp;
  const productName = data.service_info.product_name || data.service_info.brand || '';
  const map = data.competitor_map;
  const pods = usp.pod ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="slide-hdr">
        <p className="sl">Step 01 — Detail</p>
        <h2>{productName}だけの強み</h2>
        <p className="bt">市場・競合・自社を分析し、他社には真似できないポジションを導き出しました</p>
      </div>

      <div className="usp-detail-container">
        {/* Left: Logic flow */}
        <div className="usp-logic-flow">
          {/* Market + Competitors */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(4px, 0.8vmin, 8px)', flex: 1 }}>
            <div className="card" style={{ padding: 'clamp(8px, 1.2vmin, 16px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: 'clamp(8px, 1vmin, 10px)', color: 'rgba(29,25,38,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Inter, sans-serif', marginBottom: 4 }}>
                市場の動向
              </p>
              <p style={{ fontSize: 'clamp(12px, 1.7vmin, 15px)', fontWeight: 700, lineHeight: 1.5 }}>
                {pods[0] || '市場分析データ'}
              </p>
            </div>
            <div className="card" style={{ padding: 'clamp(8px, 1.2vmin, 16px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: 'clamp(8px, 1vmin, 10px)', color: 'rgba(29,25,38,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Inter, sans-serif', marginBottom: 4 }}>
                他社の状況
              </p>
              <p style={{ fontSize: 'clamp(12px, 1.7vmin, 15px)', fontWeight: 700, lineHeight: 1.5 }}>
                {(usp.pop ?? [])[0] || '競合分析データ'}
              </p>
            </div>
          </div>
          {/* Arrow down */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
            <svg width="14" height="10" viewBox="0 0 14 10">
              <polygon points="0,0 14,0 7,10" fill="rgba(29,25,38,0.15)" />
            </svg>
          </div>
          {/* Strength */}
          <div style={{ background: '#1D1926', color: '#fff', borderRadius: 8, padding: 'clamp(8px, 1.2vmin, 16px)', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <p style={{ fontSize: 'clamp(8px, 1vmin, 10px)', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Inter, sans-serif', marginBottom: 4 }}>
              {productName}の強み
            </p>
            <p style={{ fontSize: 'clamp(12px, 1.7vmin, 15px)', fontWeight: 700 }}>
              {pods.join('・')}
            </p>
          </div>
          {/* Arrow down */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
            <svg width="14" height="10" viewBox="0 0 14 10">
              <polygon points="0,0 14,0 7,10" fill="#3eb298" />
            </svg>
          </div>
          {/* USP box */}
          <div style={{ border: '8px solid #3eb298', borderRadius: 12, padding: 'clamp(12px, 2vmin, 20px)', textAlign: 'center', flex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <p style={{ fontSize: 12, color: '#3eb298', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Inter, sans-serif', marginBottom: 8 }}>
              USP
            </p>
            <p style={{ fontSize: 'clamp(20px, 3.6vmin, 28px)', fontWeight: 700, lineHeight: 1.3 }}>
              {usp.usp_statement || ''}
            </p>
          </div>
        </div>

        {/* Right: Positioning map */}
        <div className="usp-positioning-map">
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>競合とのポジショニング</p>
          <div className="card" style={{ position: 'relative', overflow: 'hidden', flex: 1, padding: 12, width: '100%', maxWidth: '100%' }}>
            {/* Axis labels */}
            <p className="map-axis-label" style={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: 'rgba(29,25,38,0.4)', fontFamily: 'Inter, sans-serif' }}>
              {map?.axis_y?.label ?? '機能'} 高い ↑
            </p>
            <p className="map-axis-label" style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: 'rgba(29,25,38,0.4)', fontFamily: 'Inter, sans-serif' }}>
              {map?.axis_y?.label ?? '機能'} 低い ↓
            </p>
            <p className="map-axis-label" style={{ position: 'absolute', left: 2, top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: 'rgba(29,25,38,0.4)', fontFamily: 'Inter, sans-serif' }}>
              ← {map?.axis_x?.label ?? '価格'}
            </p>
            <p className="map-axis-label" style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: 'rgba(29,25,38,0.4)', fontFamily: 'Inter, sans-serif' }}>
              {map?.axis_x?.label ?? '価格'} →
            </p>
            {/* Cross lines */}
            <div style={{ position: 'absolute', top: 16, bottom: 12, left: '50%', width: 1.5, background: 'rgba(29,25,38,0.12)' }} />
            <div style={{ position: 'absolute', left: 16, right: 16, top: '50%', height: 1.5, background: 'rgba(29,25,38,0.12)' }} />

            {/* Plots */}
            {map?.plots?.map((plot, i) => {
              // Convert x,y (-1 to 1 or 0 to 100) to percentage
              const xPercent = typeof plot.x === 'number'
                ? (plot.x >= -1 && plot.x <= 1 ? (plot.x + 1) * 50 : plot.x)
                : 50;
              const yPercent = typeof plot.y === 'number'
                ? (plot.y >= -1 && plot.y <= 1 ? (1 - plot.y) * 50 : 100 - plot.y)
                : 50;

              return (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${xPercent}%`,
                    top: `${yPercent}%`,
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: plot.is_self ? 44 : 32,
                      height: plot.is_self ? 44 : 32,
                      borderRadius: '50%',
                      background: plot.is_self ? '#3eb298' : '#E4E0ED',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {plot.is_self && (
                      <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>自社</span>
                    )}
                  </div>
                  <span
                    className="map-plot-label"
                    style={{
                      fontSize: plot.is_self ? 10 : 9,
                      fontWeight: plot.is_self ? 600 : 400,
                      color: plot.is_self ? '#1D1926' : 'rgba(29,25,38,0.4)',
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {plot.name}
                  </span>
                </div>
              );
            })}

            {/* Fallback if no map */}
            {!map && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(29,25,38,0.3)', fontSize: 14 }}>
                ポジショニングマップ
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Conclusion line */}
      <div style={{ borderTop: '1px solid rgba(29,25,38,0.08)', marginTop: 'clamp(8px, 1.2vmin, 12px)', paddingTop: 'clamp(8px, 1.2vmin, 12px)', textAlign: 'center' }}>
        <p style={{ fontSize: 'clamp(14px, 2.6vmin, 32px)', fontWeight: 700 }}>
          {usp.usp_statement ? (
            <>
              「{usp.usp_statement}」と言えるのは<span style={{ color: '#3eb298' }}>{productName}だけ。</span>
            </>
          ) : (
            <span style={{ color: '#3eb298' }}>{productName}の独自ポジション</span>
          )}
        </p>
      </div>
    </div>
  );
};

export default USPDetailSlide;
